import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Flex, Text, useToken } from '@bonusly/design-system';

import type { BizyChartNode } from '../../../../lib/bizy/types/nodes';
import { HighchartsExportBar } from '../lib/highcharts-export-bar';
// Importing highcharts-shared registers the Highcharts export modules (side effect).
import { resolveCssColor } from '../lib/highcharts-shared';
import { TruncationCaption } from '../lib/truncation-caption';
import { useEvidenceDrill } from '../lib/use-evidence-drill';
import type { BizyNodeProps } from './node-props';

export { resolveCssColor };

type ChartPayload = BizyChartNode['payload'];
type Series = ChartPayload['series'][number];
type YFormat = ChartPayload['yFormat'];

const isScatterLike = (variant: ChartPayload['variant']): boolean => variant === 'scatter' || variant === 'bubble';

const PALETTE_TOKENS = ['primary.bg', 'hashtag.fg', 'green.500', 'orange.400', 'red.400'];

const DIVERGING_POSITIVE = '#2e9e6b';
const DIVERGING_NEGATIVE = '#e0564f';
const REFERENCE_LINE_COLOR = '#64748b';

export function formatValue(value: number | string | null | undefined, format: YFormat): string {
	if (typeof value !== 'number' || Number.isNaN(value)) return value == null ? '' : String(value);
	switch (format) {
		case 'percent':
			return `${value}%`;
		case 'currency':
			return `$${value.toLocaleString()}`;
		default:
			return value.toLocaleString();
	}
}

function withUniqueNames(series: Series[]): Series[] {
	const seen = new Map<string, number>();
	return series
		.filter((s) => s.name?.trim().length > 0)
		.map((s) => {
			const count = (seen.get(s.name) ?? 0) + 1;
			seen.set(s.name, count);
			return count === 1 ? s : { ...s, name: `${s.name} (${count})` };
		});
}

function distinctLabels(series: Series[]): string[] {
	const seen = new Set<string>();
	const labels: string[] = [];
	series.forEach((s) =>
		s.points.forEach((p) => {
			if (p.label && !seen.has(p.label)) {
				seen.add(p.label);
				labels.push(p.label);
			}
		})
	);
	return labels;
}

function pointAt(series: Series, label: string) {
	return series.points.find((p) => p.label === label);
}

interface BuildOptionsArgs {
	variant: ChartPayload['variant'];
	yFormat: YFormat;
	series: Series[];
	palette: string[];
	axisColor: string;
	gridColor: string;
	surfaceColor: string;
	xLabel?: string;
	yLabel?: string;
	sizeLabel?: string;
	fallbackLabels?: { entity: string; size: string };
	referenceValue?: number;
	referenceLabel?: string;
	onPointClick?: (label: string, seriesName?: string) => void;
}

export function buildChartOptions({
	variant,
	yFormat,
	series,
	palette,
	axisColor,
	gridColor,
	surfaceColor,
	xLabel,
	yLabel,
	sizeLabel,
	fallbackLabels = { entity: 'Entity', size: 'Size' },
	referenceValue,
	referenceLabel,
	onPointClick,
}: BuildOptionsArgs): Highcharts.Options {
	const visible = withUniqueNames(series);
	const labels = distinctLabels(visible);
	const stacked = variant === 'stacked';
	const diverging = variant === 'diverging';
	const scatterLike = isScatterLike(variant);
	const bubble = variant === 'bubble';
	const isBar = variant === 'bar' || stacked || diverging;
	const showValueAxis = !isBar || stacked || diverging;
	const colorAt = (i: number) => palette[i % palette.length];

	const dataSeries = (
		scatterLike
			? visible.map((s, i) => ({
					type: bubble ? 'bubble' : 'scatter',
					name: s.name,
					color: colorAt(i),
					marker: { lineWidth: 1, lineColor: surfaceColor },
					data: s.points.map((p) => ({
						x: p.x ?? null,
						y: p.y ?? null,
						...(bubble ? { z: p.z ?? null } : {}),
						name: p.label,
						custom: { partial: !!p.partial },
					})),
				}))
			: visible.map((s, i) => ({
					type: isBar ? 'bar' : 'line',
					name: s.name,
					...(diverging ? {} : { color: colorAt(i) }),
					data: labels.map((label) => {
						const point = pointAt(s, label);
						const y = point?.value ?? null;
						const cell = { y, custom: { partial: !!point?.partial } };
						return diverging && typeof y === 'number' ? { ...cell, color: y < 0 ? DIVERGING_NEGATIVE : DIVERGING_POSITIVE } : cell;
					}),
					...(isBar ? {} : { marker: { enabled: true, radius: 3 } }),
				}))
	) as Highcharts.SeriesOptionsType[];

	const plotLines: Highcharts.YAxisPlotLinesOptions[] =
		referenceValue == null
			? []
			: [
					{
						value: referenceValue,
						color: REFERENCE_LINE_COLOR,
						dashStyle: 'Dash',
						width: 1.5,
						zIndex: 4,
						label: referenceLabel
							? { useHTML: true, text: `${referenceLabel}: ${formatValue(referenceValue, yFormat)}`, style: { color: axisColor, fontSize: '11px' } }
							: undefined,
					},
				];

	const valueLabel = function (this: Highcharts.Point): string {
		const formatted = formatValue(this.y ?? null, yFormat);
		return (this.options?.custom?.partial as boolean | undefined) ? `${formatted}*` : formatted;
	};

	return {
		chart: { type: scatterLike ? variant : isBar ? 'bar' : 'line', backgroundColor: 'transparent', spacing: [8, 8, 8, 8] },
		title: { text: '' },
		credits: { enabled: false },
		exporting: scatterLike
			? {
					enabled: false,
					csv: {
						columnHeaderFormatter(item: { coll?: string } | null, key?: string) {
							if (!item || item.coll === 'xAxis') return fallbackLabels.entity;
							if (key === 'x') return xLabel ?? 'X';
							if (key === 'y') return yLabel ?? 'Y';
							if (key === 'z') return sizeLabel ?? fallbackLabels.size;
							return false;
						},
					},
				}
			: { enabled: false },
		legend: { enabled: visible.length > 1, useHTML: true, itemStyle: { color: axisColor, fontWeight: 'normal' } },
		xAxis: scatterLike
			? {
					title: { text: '' },
					lineColor: gridColor,
					tickColor: gridColor,
					gridLineColor: gridColor,
					labels: {
						useHTML: true,
						style: { color: 'var(--chakra-colors-text-muted)', fontSize: '11px' },
						formatter: function () {
							return formatValue(this.value as number, 'number');
						},
					},
				}
			: {
					categories: labels,
					lineColor: gridColor,
					tickColor: gridColor,
					labels: { useHTML: true, step: isBar ? 1 : undefined, style: { color: 'var(--chakra-colors-text-muted)', fontSize: '11px' } },
				},
		yAxis: {
			title: { text: '' },
			min: diverging || scatterLike ? undefined : 0,
			gridLineColor: gridColor,
			plotLines: diverging ? [{ value: 0, color: axisColor, width: 1, zIndex: 3 }, ...plotLines] : plotLines,
			visible: showValueAxis,
			labels: showValueAxis
				? {
						useHTML: true,
						style: { color: 'var(--chakra-colors-text-muted)', fontSize: '11px' },
						formatter: function () {
							return formatValue(this.value as number, yFormat);
						},
					}
				: { enabled: false },
		},
		tooltip: {
			useHTML: true,
			outside: true,
			style: { zIndex: 9999 },
			formatter: function (this: Highcharts.Point) {
				if (scatterLike) {
					const entity = this.name ?? '';
					const seriesPrefix = visible.length > 1 && this.series?.name ? `${this.series.name}<br/>` : '';
					const parts = [`${xLabel ?? 'x'}: ${formatValue(this.x as number, 'number')}`, `${yLabel ?? 'y'}: ${formatValue(this.y ?? null, yFormat)}`];
					if (bubble) parts.push(`${sizeLabel ?? fallbackLabels.size}: ${formatValue((this as { z?: number }).z ?? null, 'number')}`);
					return `<b>${entity}</b><br/>${seriesPrefix}${parts.join('<br/>')}`;
				}
				const label = this.key ?? '';
				const name = this.series?.name ? `${this.series.name}: ` : '';
				return `<b>${label}</b><br/>${name}${formatValue(this.y ?? null, yFormat)}`;
			},
		},
		plotOptions: {
			bar: {
				stacking: stacked ? 'normal' : undefined,
				pointPadding: 0.2,
				groupPadding: 0.25,
				borderRadius: { radius: 3, where: 'all', scope: 'point' },
				dataLabels: {
					enabled: !stacked,
					inside: false,
					align: 'left',
					overflow: 'allow',
					crop: false,
					formatter: valueLabel,
					style: { fontWeight: 'normal', color: 'var(--chakra-colors-text-default)', textOutline: 'none' },
				},
			},
			line: { dataLabels: { enabled: false } },
			...(onPointClick
				? {
						series: {
							cursor: 'pointer',
							point: {
								events: {
									click: function (this: Highcharts.Point & { category?: string | number; series?: { name?: string } }) {
										onPointClick(String(this.name ?? this.category ?? ''), this.series?.name);
									},
								},
							},
						},
					}
				: {}),
		},
		series: dataSeries,
	};
}

/**
 * Renders the Data Agent's Chart node with Highcharts. `bar` for
 * rankings/breakdowns, `line` for a metric over time, scatter/bubble for
 * two-metric plots. A hover-revealed action bar exports CSV / PNG.
 */
export function ChartNode({ node }: BizyNodeProps) {
	const { payload } = node as BizyChartNode;
	const { variant, title, xLabel, yLabel, sizeLabel, yFormat, series, referenceValue, referenceLabel, totalGroups, selection } = payload;
	const chartRef = useRef<HighchartsReact.RefObject>(null);
	const { t } = useTranslation('admin_bizy');
	const { drillEnabled, drill, expand } = useEvidenceDrill();
	const palette = useToken('colors', PALETTE_TOKENS).map(resolveCssColor);
	const [axisColor, gridColor, surfaceColor] = useToken('colors', ['text.muted', 'border.soft', 'bg.surface']).map(resolveCssColor);

	const visibleSeries = withUniqueNames(series);
	const labels = distinctLabels(visibleSeries);
	const scatterLike = isScatterLike(variant);
	const hasPlottableData = scatterLike ? visibleSeries.some((s) => s.points.length > 0) : labels.length > 0;
	if (visibleSeries.length === 0 || !hasPlottableData) return null;

	const onDrill = drillEnabled
		? (category: string, seriesName?: string) => {
				const entity = series.length > 1 && seriesName ? `${seriesName} for ${category}` : category;
				drill('chart', entity, { title, variant });
			}
		: undefined;
	const onExpand =
		drillEnabled && totalGroups != null
			? () => expand('chart', totalGroups as number, { title, noun: variant === 'scatter' || variant === 'bubble' ? undefined : xLabel })
			: undefined;

	const options = buildChartOptions({
		variant,
		yFormat,
		series,
		palette,
		axisColor,
		gridColor,
		surfaceColor,
		xLabel,
		yLabel,
		sizeLabel,
		fallbackLabels: { entity: t('chartFallback.entity'), size: t('chartFallback.size') },
		referenceValue,
		referenceLabel,
		onPointClick: onDrill,
	});

	const shownGroups = scatterLike ? withUniqueNames(series).reduce((n, s) => n + s.points.length, 0) : distinctLabels(withUniqueNames(series)).length;

	const barsPerCategory = variant === 'bar' ? visibleSeries.length : 1;
	const height = variant === 'line' ? 260 : scatterLike ? 320 : labels.length * (barsPerCategory * 20 + 28) + 64;

	const axisCaption = scatterLike
		? [yLabel && `y: ${yLabel}`, xLabel && `x: ${xLabel}`, variant === 'bubble' && sizeLabel && `size: ${sizeLabel}`].filter(Boolean).join('  \u00b7  ')
		: yLabel;

	return (
		<Flex direction="column" gap={2} mb={4} data-testid="bizy-chart" sx={{ '&:hover .bizy-chart-actions': { opacity: 1 } }}>
			{title ? (
				<Text fontSize="md" fontWeight="semibold" color="text.default">
					{title}
				</Text>
			) : null}
			<Box width="100%" sx={{ '.highcharts-container': { margin: '0 auto' } }}>
				<HighchartsReact ref={chartRef} highcharts={Highcharts} options={{ ...options, chart: { ...options.chart, height } }} />
			</Box>
			{axisCaption ? (
				<Text fontSize="xs" color="text.muted" textAlign="center" textTransform="uppercase" letterSpacing="0.08em" fontWeight="semibold" mt={1}>
					{axisCaption}
				</Text>
			) : null}
			{totalGroups != null ? <TruncationCaption shown={shownGroups} total={totalGroups} selection={selection} onExpand={onExpand} /> : null}
			<HighchartsExportBar
				chartRef={chartRef}
				title={title}
				xLabel={xLabel}
				yLabel={yLabel}
				subtitle={variant === 'bubble' && sizeLabel ? `Bubble size: ${sizeLabel}` : undefined}
				fallback="chart"
				variant={variant}
				idPrefix="bizy-chart"
			/>
		</Flex>
	);
}
